import { _ } from "../../../../../core/polyglot/polyglot.js";
import {
	badge,
	spacer,
	stacker,
	text,
	theme,
} from "../../../../../core/elements/index.js";
import {
	centerAlign,
	leftAlign,
	rightAlign,
} from "../../../../../core/elements/elementAlignement.js";
import {
	ChangeTypeState,
	EditState,
	ReadOnlyState,
} from "./RuleTemplatesState.js";
import { large, small } from "../../../../../core/elements/size/index.js";
import { trashAltIcon } from "../../../../../core/elements/icons/index.js";
import { verticalDirection } from "../../../../../core/elements/stacker/stacker.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ActionButtonWidget from "../../../../../core/widgets/ActionButtonWidget/ActionButtonWidget.js";
import addBalanceRuleModelFieldsToForm from "../ruleModelEditors/addBalanceRuleModelFieldsToForm.js";
import addPredictionRuleModelFieldsToForm from "../ruleModelEditors/addPredictionRuleModelFieldsToForm.js";
import AddRuleTemplateAction from "./actions/AddRuleTemplateAction.js";
import BulkRuleTemplateAction from "./actions/BulkRuleTemplateAction.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import FlipButton from "../../../../../core/widgets/FlipButton/FlipButton.js";
import FormBuilder from "../../../../../core/widgets/forms/FormBuilder.js";
import NullRuleTemplateAction from "./actions/NullRuleTemplateAction.js";
import RemoveRuleTemplateAction from "./actions/RemoveRuleTemplateAction.js";
import RuleTemplateDescriptionWidget from "../RuleTemplateDescriptionWidget/RuleTemplateDescriptionWidget.js";
import RuleTemplateWidget from "../RuleTemplateWidget/RuleTemplateWidget.js";
import UpdateRuleTemplateAction from "./actions/UpdateRuleTemplateAction.js";
import WysiwygEditorWidget from "../../../../../core/widgets/WysiwygEditorWidget/WysiwygEditorWidget.js";

/**
 * Display 3 `RuleTemplateWidget`: one for a prediction rule model, one for a
 * VAT rule model and one for a cash conversion rule model.
 *
 * @param {RuleTemplatesModel} ruleTemplatesModel - Model to pilot this widget
 * @param {Budget[]} budgets - Budgets for a report
 * @param {VatRate[]} vatRates - List of available VatRates
 * @param {function} onCellSelectionRequest - Function called with
 *   {row,column} as argument when the user wants this cell to be
 *   selected
 * @param {function} onBulkActionRequest - Function called with
 *   {bulkAction} as argument when the user is done editing rules
 * @param {boolean} [isEditing] - When true, the content is in edition mode
 *   with some sort of forms to edit the rule templates. This only allows
 *   to change the value of a rule, not its type
 * @param {boolean} [isChangingType] - When true, the content is in change type
 *   mode. It's like creating a new rule from scratch, but it modifies an
 *   existing rule
 * @param {boolean} [readOnly] - If false the buttons 'Add', 'Edit', 'Delete' and 'Override',
 * do not exist.
 * @param {Class[]} availableRuleModelClasses - List of RuleModel classes that
 *   can be used to add a new ruleModel to the selected cell
 * @param {AbstractRuleTemplateState} currentState - Used to coordinate the rendering
 *   with ManualEntryRuleTemplatesWidget.
 */
export default class RuleTemplatesWidget extends Widget {
	constructor({
		ruleTemplatesModel,
		budgets,
		vatRates,
		onCellSelectionRequest,
		onBulkActionRequest,
		isEditing = false,
		isChangingType = false,
		readOnly,
		availableRuleModelClasses,
		currentState,
	}) {
		super(...arguments);

		this._budgets = budgets;
		this._vatRates = vatRates;
		this._onCellSelectionRequest = onCellSelectionRequest;
		this._onBulkActionRequest = onBulkActionRequest;
		this._currentState = currentState;

		this.setRuleTemplatesModel(ruleTemplatesModel, {
			silently: true,
		});

		if (readOnly) {
			this._currentState = new ReadOnlyState({
				ruleTemplatesWidget: this,
			});
		} else if (isEditing) {
			this._currentState = new EditState();
		} else if (isChangingType) {
			this._currentState = new ChangeTypeState();
		}

		this._currentState.addWidget(this);

		this._availableRuleModelClasses = availableRuleModelClasses;

		this._predictionRuleTemplateWidget =
			this._makePredictionRuleTemplateWidget();

		this._balanceRuleTemplateWidget = this._makeBalanceRuleTemplateWidget();

		this._ruleTemplateDescriptionWidget =
			this._makeRuleTemplateDescriptionWidget();
	}

	isEmpty() {
		return this._ruleTemplatesModel.isEmpty();
	}

	// We don't follow the guideline here when it comes to using an object parameter
	// as otherwise it makes calling this method really confusing, and the `silently` parameter
	// is only used locally.
	setRuleTemplatesModel(ruleTemplatesModel, { silently = false } = {}) {
		this._ruleTemplatesModel = ruleTemplatesModel;

		this._predictionRuleTemplateWidget?.setRuleTemplateModel(
			this._ruleTemplatesModel.getPredictionRuleTemplateModel(),
		);

		this._predictionRuleTemplateWidget?.setEditor(
			this._makePredictionRuleEditor(),
		);

		this._balanceRuleTemplateWidget?.setRuleTemplateModel(
			this._ruleTemplatesModel.getBalanceRuleTemplateModel(),
		);

		this._balanceRuleTemplateWidget?.setEditor(
			this._makeBalanceRuleEditor(),
		);

		this._ruleTemplateDescriptionWidget?.setRuleTemplateModel(
			this._ruleTemplatesModel.getPredictionRuleTemplateModel(),
		);

		if (!silently) {
			this.update();
		}
	}

	changeState({ state }) {
		this._currentState = state;
		this._predictionRuleTemplateWidget.setState(state);
		this._ruleTemplateDescriptionWidget.setState(state);
		this._balanceRuleTemplateWidget.setState(state);
		this.update();
	}

	makeBulkEditAction(actions) {
		const bulkAction = new BulkRuleTemplateAction();
		bulkAction.appendActions(actions);

		return bulkAction;
	}

	makeNullRuleTemplateAction() {
		return new NullRuleTemplateAction();
	}

	makeAddRuleTemplateAction(ruleTemplate) {
		return new AddRuleTemplateAction({ ruleTemplate });
	}

	makeUpdateRuleTemplateAction(ruleTemplate) {
		return new UpdateRuleTemplateAction({ ruleTemplate });
	}

	makeRemoveRuleTemplateAction(ruleTemplate) {
		return new RemoveRuleTemplateAction({ ruleTemplate });
	}

	renderContentOn(html) {
		html.div(
			{ klass: "templates-container" },
			this._renderPredictionRuleTitleOn.bind(this),
			this._renderPredictionRuleTemplateOn.bind(this),
			this._renderDescriptionOn.bind(this),
			this._renderBalanceRuleTemplateOn.bind(this),
			this._renderButtonsOn.bind(this),
		);
	}

	renderButtonsOwnOn(html) {
		html.render(this._currentState.ownRuleButtonsRenderer(this));
	}

	renderButtonsInheritedOn(html) {
		html.render(this._currentState.inheritedRuleButtonsRenderer(this));
	}

	renderEditInheritedRuleButtonsOn(html) {
		html.div(
			{ class: "override-button" },
			new ActionButtonWidget({
				dataTestName: "override",
				isPrimary: true,
				label: _("Override rule"),
				click: () => this._currentState.goToOverrideState(),
			}),
		);
	}

	renderCreateButtonsOn(html) {
		html.div(
			spacer(
				{
					direction: verticalDirection,
					align: "left",
					spacing: "between",
					fullWidth: true,
				},
				html.h2(this._currentState.getTitle()),
				this._makeAddRuleButtons(),
			),
		);
	}

	renderCreateButtonsWithCancelOn(html) {
		this.renderCreateButtonsOn(html);

		html.render(
			spacer(
				{ isBlock: true, top: true, align: rightAlign, size: large },
				button(
					{
						link: true,
						dataTest: "cancel",
						onClick: () => this._currentState.goToViewState(),
					},
					_("Cancel"),
				),
			),
		);
	}

	renderEditingPredictionRuleTitleOn(html) {
		html.h2(
			stacker(
				{
					class: "rule-title",
					align: { horizontal: leftAlign, vertical: centerAlign },
					dataTest: "prediction-rule-template",
				},
				this._ruleTemplatesModel.getPredictionRuleTemplateLabel(),
				this._ruleTemplatesModel.isBeta() &&
					badge({ label: _("Beta") }),
			),
		);
	}

	renderNonEditButtonsOn(html) {
		html.render(this._ruleTemplatesModel.renderButtonsFor(this));
	}

	makeOwnButtons() {
		return spacer(
			{ fullWidth: true, top: true },
			button(
				{
					dataTest: "delete",
					danger: true,
					onClick: this._onDeleteRequest.bind(this),
				},
				_("Delete"),
			),
			button(
				{
					dataTest: "change-type",
					onClick: () => this._currentState.goToChangeTypeState(),
				},
				_("Change rule"),
			),
			button(
				{
					dataTest: "edit",
					primary: true,
					onClick: () => this._currentState.goToEditState(),
				},
				_("Edit"),
			),
		);
	}

	renderEditButtonsOn(html) {
		html.div(
			{ class: "edit-buttons" },
			html.span(
				_("{save} or {cancel}", {
					save: html.button(
						{
							"data-test": "save",
							klass: "btn btn-primary",
							click: () => this._onSaveRequest(),
						},
						_("Save"),
					),
					cancel: html.a(
						{
							class: "cancel",
							"data-test": "cancel",
							click: () => {
								this._currentState.goToViewState();
							},
						},
						_("Cancel"),
					),
				}),
			),
		);
	}

	renderEditPredictionRuleTemplateOn(html) {
		html.div(
			{ class: "drawer-section darker", "data-test": "prediction" },
			this._predictionRuleTemplateWidget,
		);
	}

	renderEditBalanceRuleTemplateOn(html) {
		html.div(
			{ class: "drawer-section darker", "data-test": "balance" },
			this._balanceRuleTemplateWidget,
		);
	}

	renderDescriptionOn(html) {
		html.div(
			{ class: "drawer-section darker", "data-test": "description" },
			this._ruleTemplateDescriptionWidget,
		);
	}

	_renderPredictionRuleTitleOn(html) {
		html.render(this._currentState.predictionRuleTitleRenderer(this));
	}

	_renderButtonsOn(html) {
		html.render(this._currentState.buttonsRenderer(this));
	}

	_renderPredictionRuleTemplateOn(html) {
		html.render(this._currentState.predictionRuleTemplateRenderer(this));
	}

	_renderBalanceRuleTemplateOn(html) {
		html.render(this._currentState.balanceRuleTemplateRenderer(this));
	}

	_renderDescriptionOn(html) {
		html.render(this._currentState.descriptionRenderer(this));
	}

	_makePredictionRuleTemplateWidget() {
		return this._makeRuleTemplateWidget({
			editorDataTestName: "prediction-model-editor",
			predictionModelEditorClass: "prediction-model-editor",
			ruleTemplateModel:
				this._ruleTemplatesModel.getPredictionRuleTemplateModel(),
			editor: this._makePredictionRuleEditor(),
		});
	}

	_makeBalanceRuleTemplateWidget() {
		return this._makeRuleTemplateWidget({
			editorDataTestName: "balance-rule-editor",
			ruleTemplateModel:
				this._ruleTemplatesModel.getBalanceRuleTemplateModel(),
			editor: this._makeBalanceRuleEditor(),
		});
	}

	_makeRuleTemplateDescriptionWidget() {
		return new RuleTemplateDescriptionWidget({
			ruleTemplateModel:
				this._ruleTemplatesModel.getPredictionRuleTemplateModel(),
			state: this._currentState,
		});
	}

	_makePredictionRuleEditor() {
		const ruleModel = this._ruleTemplatesModel.getPredictionRuleModel();
		const form = this._makeForm({
			ruleModel,
			class: "prediction-rule-form",
		});

		form.append((html) => html.h4(_("Calculation")));
		this._ruleTemplatesModel.setPredictionForm(form);

		addPredictionRuleModelFieldsToForm({
			form,
			ruleModel,
			budgets: this._budgets,
		});

		form.addRenderable({
			name: "description",
			content: this._makeAddDescriptionButton({
				ruleModel,
			}),
			accessor: () => ruleModel.getDescription(),
			mutator: (description) => ruleModel.setDescription(description),
			getFormValue: () => ruleModel.getDescription(),
			setFormValue: (description) =>
				ruleModel.setDescription(description),
		});

		return form;
	}

	_makeAddDescriptionButton({ ruleModel }) {
		const flipButton = new FlipButton({
			buttonSpec: {
				isPrimary: false,
				dataTestName: "add-description",
				label: _("Add description"),
			},
			content: (html) =>
				html.div({ class: "description-editor" }, [
					html.h4(_("Description")),
					new WysiwygEditorWidget({
						content: () => ruleModel.getDescription(),
						onChange: (description) =>
							ruleModel.setDescription(description),
					}),
					html.div(
						{ class: "delete-button" },
						button(
							{
								dataTest: "delete-description",
								onClick: () => {
									ruleModel.setDescription("");
									flipButton.update();
									flipButton.showButton();
								},
							},
							trashAltIcon,
						),
					),
				]),
		});

		if (ruleModel.getDescription() !== "") {
			flipButton.showContent();
		}

		return flipButton;
	}

	_makeBalanceRuleEditor() {
		const ruleModel = this._ruleTemplatesModel.getBalanceRuleModel();
		const form = this._makeForm({
			ruleModel,
			class: "balance-rule-form",
		});
		form.append((html) => html.h4(_("Settings")));
		this._ruleTemplatesModel.setBalanceForm(form);

		addBalanceRuleModelFieldsToForm({
			form,
			ruleModel,
			vatRates: this._vatRates,
		});

		return form;
	}

	_makeRuleTemplateWidget({ ruleTemplateModel, editor, editorDataTestName }) {
		return new RuleTemplateWidget({
			budgets: this._budgets,
			ruleTemplateModel,
			editor,
			editorDataTestName,
			state: this._currentState,
			onCellSelectionRequest: () =>
				this._onCellSelectionRequest({
					row: ruleTemplateModel.getInheritedRow(),
					column: ruleTemplateModel.getInheritedColumn(),
				}),
		});
	}

	_makeForm({ ruleModel, class: cssClass }) {
		return new FormBuilder({
			model: ruleModel,
			klass: [cssClass, "rule-form"],
			dataTestName: "save",
			showActions: false,
			showCancel: false,
			inputClass: "col-sm-12",
			labelClass: "col-sm-12",
		});
	}

	_makeAddRuleButtons() {
		const buttons = this._availableRuleModelClasses.map(
			(PredictionModelClass) => {
				const label = PredictionModelClass.getLabel();
				const isBeta = PredictionModelClass.isBeta();
				const description = PredictionModelClass.getDescriptionText();
				return button(
					{
						as: "div",
						onClick: () => {
							this._ruleTemplatesModel.setPredictionRuleModel(
								new PredictionModelClass(),
							);

							this.setRuleTemplatesModel(
								this._ruleTemplatesModel,
								{
									silently: true,
								},
							);

							this._currentState.goToEditState();
						},
						fullWidth: true,
						class: "rule-button",
					},
					stacker(
						{
							size: small,
							direction: verticalDirection,
							align: leftAlign,
							fullWidth: true,
						},
						stacker(
							{
								align: {
									horizontal: leftAlign,
									vertical: centerAlign,
								},
							},
							text({ emphasize: true, important: true }, label),
							isBeta && badge({ label: _("Beta") }),
						),
						text({ subtle: true }, description),
					),
				);
			},
		);

		return theme(
			{ name: "light-card", isBlock: true },
			stacker(
				{ direction: verticalDirection, fullWidth: true },
				...buttons,
			),
		);
	}

	_execDelete() {
		const bulkAction = this._ruleTemplatesModel.makeDeleteActionOn(this);
		this._onBulkActionRequest({ bulkAction });
	}

	_onDeleteRequest() {
		this._confirmDeletion({
			title: _("Delete local rules?"),
			body: _("Are you sure you want to delete all the local rules?"),
			callback: this._execDelete.bind(this),
		});
	}

	_onSaveRequest() {
		const bulkAction = this._ruleTemplatesModel.makeSaveActionOn(this);

		bulkAction.perform((action) => {
			this._currentState.goToViewState();
			this._onBulkActionRequest({ bulkAction: action });
		});
	}
}
